import { Alert } from "react-native";
import * as Sentry from "@sentry/react-native";

import { checkAndAlertPhotoGrantedPermission } from "@/utils/permissions";
import { universalImagePicker } from "@/utils/image/universalImagePicker";
import MediaService from "@/services/user/media";
import { type UserImageMedia, UserImageType } from "@/schemas/user/image";
import type { AppUserSession } from "@/schemas/auth/session";

type ImageUploadOptions = {
  session: AppUserSession;
  imageType: UserImageType;
  aspectRatio?: {
    width: number;
    height: number;
  };
  compressionQuality?: number;
  onPickerCancel?: () => void;
  onUploadStart?: () => void;
  onUploadSuccess?: (images: UserImageMedia[]) => void;
  onUploadError?: (error: unknown) => void;
  onUploadEnd?: () => void;
};

const universalUploadImage = async ({
  session,
  imageType,
  aspectRatio = { width: 9, height: 16 },
  compressionQuality = 0.8,
  onPickerCancel,
  onUploadStart,
  onUploadSuccess,
  onUploadError,
  onUploadEnd,
}: ImageUploadOptions): Promise<void> => {
  const isGranted = await checkAndAlertPhotoGrantedPermission();
  if (!isGranted) {
    return;
  }

  const handleUploadError = (
    error: unknown,
    onUploadError?: (error: unknown) => void,
  ): void => {
    if (onUploadError) {
      onUploadError(error);
    } else {
      Sentry.captureException(error);

      Alert.alert(
        "Something went wrong. ",
        "Our team has been notified. Please try again later.",
        [{ text: "OK" }],
      );
    }
  };

  try {
    const uris = await universalImagePicker({
      imageType,
      aspectRatio,
      compressionQuality,
      ...(onPickerCancel ? { onPickerCancel } : {}),
    });

    if (!uris) {
      return;
    }

    onUploadStart?.();

    const ms = new MediaService(session);

    const response = await ms.uploadPublicImages(uris, imageType);

    if (response.items.length === 0) {
      throw new Error(
        `MediaService.uploadPublicImages response.items.length is 0 for ${imageType}`,
      );
    } else if (response.items.length > 1) {
      // TODO: need to remove this when multiple selection is supported
      throw new Error(
        `Unexpected number of images uploaded in universalUploadImage for ${imageType}`,
      );
    }

    // TODO: currently we only support uploading one image at a time
    // we need to handle more than one paginated response if otherwise
    onUploadSuccess?.(response.items);
  } catch (error) {
    handleUploadError(error, onUploadError);
  } finally {
    onUploadEnd?.();
  }
};

export default universalUploadImage;
